import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FiFileText, FiSend, FiCheck, FiClock, FiAlertCircle, 
  FiTrendingUp, FiUsers, FiDollarSign, FiCalendar,
  FiRefreshCw, FiDownload, FiFilter, FiSearch,
  FiMail, FiMessageSquare, FiEye, FiEdit3,
  FiPieChart, FiBarChart2, FiActivity, FiX
} from 'react-icons/fi';
import { 
  FaSignature, FaFileContract, FaChartLine, FaUserCheck,
  FaExclamationTriangle, FaCheckDouble, FaHistory
} from 'react-icons/fa';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';

const ContractDashboard = () => {
  const [activeView, setActiveView] = useState('overview');
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    sent: 0,
    viewed: 0,
    signed: 0,
    completed: 0,
    expired: 0,
    avgTimeToSign: 0,
    todaysSent: 0,
    weeklyCompletion: 0,
    monthlyRevenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState('30days');
  const [tableError, setTableError] = useState(false);

  useEffect(() => {
    loadContracts();
  }, [filterStatus, dateRange]);

  const loadContracts = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('contracts')
        .select('*')
        .order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data, error } = await query;
      
      if (error) {
        console.error('Error loading contracts:', error);
        // Check if table doesn't exist
        if (error.message?.includes('relation "public.contracts" does not exist')) {
          console.error('Contracts table not found! Please run contracts-table-setup.sql in Supabase');
          setTableError(true);
        }
        setContracts([]);
        calculateStats([]);
        return;
      }
      
      setTableError(false);

      setContracts(data || []);
      calculateStats(data || []);
    } catch (err) {
      console.error('Error loading contracts:', err);
      setContracts([]);
      calculateStats([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (contractData) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(now.setDate(now.getDate() - 7));
    const monthAgo = new Date(now.setMonth(now.getMonth() - 1));

    const stats = {
      total: contractData.length,
      sent: contractData.filter(c => c.status === 'sent').length,
      viewed: contractData.filter(c => c.status === 'viewed').length,
      signed: contractData.filter(c => c.status === 'signed').length,
      completed: contractData.filter(c => c.status === 'completed').length,
      expired: contractData.filter(c => c.status === 'expired').length,
      todaysSent: contractData.filter(c => 
        c.sent_at && new Date(c.sent_at) >= today
      ).length,
      weeklyCompletion: contractData.filter(c => 
        c.status === 'completed' && c.completed_at && new Date(c.completed_at) >= weekAgo
      ).length,
      monthlyRevenue: contractData
        .filter(c => c.created_at && new Date(c.created_at) >= monthAgo)
        .reduce((sum, c) => sum + (c.contract_value || 0), 0),
      avgTimeToSign: calculateAverageTimeToSign(contractData)
    };

    setStats(stats);
  };

  const calculateAverageTimeToSign = (contracts) => {
    const signedContracts = contracts.filter(c => c.signed_at && c.sent_at);
    if (signedContracts.length === 0) return 0;

    const totalHours = signedContracts.reduce((sum, c) => {
      const sent = new Date(c.sent_at);
      const signed = new Date(c.signed_at);
      const hours = (signed - sent) / (1000 * 60 * 60);
      return sum + hours;
    }, 0);

    return Math.round(totalHours / signedContracts.length);
  };

  const resendContract = async (contractId) => {
    try {
      const { error } = await supabase
        .from('contracts')
        .update({
          last_reminder_at: new Date().toISOString()
        })
        .eq('id', contractId);

      if (error) throw error;
      alert('Contract resent successfully');
      loadContracts();
    } catch (err) {
      console.error('Error resending contract:', err);
      alert('Failed to resend contract');
    }
  };

  const voidContract = async (contractId) => {
    if (!confirm('Are you sure you want to void this contract? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('contracts')
        .update({
          status: 'voided',
          voided_at: new Date().toISOString()
        })
        .eq('id', contractId);

      if (error) throw error;
      alert('Contract voided successfully');
      loadContracts();
    } catch (err) {
      console.error('Error voiding contract:', err);
      alert('Failed to void contract');
    }
  };

  const exportContracts = () => {
    const csv = convertToCSV(contracts);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contracts_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const convertToCSV = (data) => {
    const headers = ['Contract Number', 'Customer', 'Type', 'Status', 'Created', 'Sent', 'Signed', 'Value'];
    const rows = data.map(c => [
      c.contract_number || `#${c.id?.slice(0, 8)}`,
      c.customer_name || '',
      c.contract_type || '',
      c.status || '',
      c.created_at ? new Date(c.created_at).toLocaleDateString() : '',
      c.sent_at ? new Date(c.sent_at).toLocaleDateString() : '',
      c.signed_at ? new Date(c.signed_at).toLocaleDateString() : '',
      c.contract_value || 0
    ]);
    
    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  const getStatusIcon = (status) => {
    const icons = {
      draft: FiEdit3,
      sent: FiSend,
      viewed: FiEye,
      signed: FaSignature,
      completed: FiCheck,
      expired: FiClock,
      voided: FiAlertCircle
    };
    return icons[status] || FiFileText;
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      viewed: 'bg-purple-100 text-purple-800',
      signed: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      voided: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Contract Management</h2>
            <p className="text-gray-600">Monitor and manage all contracts</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={exportContracts}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <SafeIcon icon={FiDownload} className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={() => loadContracts()}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Total Contracts</p>
            <SafeIcon icon={FiFileText} className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-xs text-gray-500 mt-1">All time</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Pending Signature</p>
            <SafeIcon icon={FiClock} className="w-5 h-5 text-yellow-500" />
          </div>
          <p className="text-2xl font-bold text-yellow-600">{stats.sent + stats.viewed}</p>
          <p className="text-xs text-gray-500 mt-1">Awaiting action</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Completed</p>
            <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
          <p className="text-xs text-gray-500 mt-1">This month</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Avg. Time to Sign</p>
            <SafeIcon icon={FiActivity} className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-blue-600">{stats.avgTimeToSign}h</p>
          <p className="text-xs text-gray-500 mt-1">Average hours</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Contract Value</p>
            <SafeIcon icon={FiDollarSign} className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-green-600">
            ${(stats.monthlyRevenue / 1000).toFixed(0)}k
          </p>
          <p className="text-xs text-gray-500 mt-1">This month</p>
        </motion.div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search contracts..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="viewed">Viewed</option>
            <option value="signed">Signed</option>
            <option value="completed">Completed</option>
            <option value="expired">Expired</option>
          </select>

          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="year">Last Year</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>

      {/* Table Error Message */}
      {tableError && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-600 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Database Setup Required</h3>
              <p className="mt-2 text-sm text-red-700">
                The contracts table hasn't been created yet. Please run the following SQL in your Supabase SQL Editor:
              </p>
              <div className="mt-2 p-2 bg-red-100 rounded text-xs font-mono">
                contracts-table-setup.sql
              </div>
              <p className="mt-2 text-sm text-red-700">
                This will create the contracts table with sample data.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Contracts Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : contracts.length === 0 ? (
          <div className="p-8 text-center">
            <div className="max-w-md mx-auto">
              <SafeIcon icon={FiFileText} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Contracts Yet</h3>
              <p className="text-gray-500 mb-4">
                Contracts are created from completed deals. 
              </p>
              <div className="bg-blue-50 rounded-lg p-4 text-left">
                <p className="text-sm font-medium text-blue-900 mb-2">To create contracts:</p>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Go to Deals → Deal Management</li>
                  <li>Open the DealBuilder tab</li>
                  <li>Create and save a deal</li>
                  <li>Click "Create Contracts" button</li>
                  <li>Select templates and send to customer</li>
                </ol>
              </div>
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg text-left">
                <p className="text-xs text-yellow-800">
                  <strong>Note:</strong> If you're seeing an error, you may need to run 
                  <code className="bg-yellow-100 px-1 mx-1">contracts-advanced-setup.sql</code> 
                  in Supabase first.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contract</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contracts
                  .filter(c => {
                    if (!searchTerm) return true;
                    const search = searchTerm.toLowerCase();
                    return (
                      c.contract_number?.toLowerCase().includes(search) ||
                      c.customer_name?.toLowerCase().includes(search) ||
                      c.customer_email?.toLowerCase().includes(search) ||
                      c.contract_type?.toLowerCase().includes(search)
                    );
                  })
                  .map((contract) => {
                    const StatusIcon = getStatusIcon(contract.status);
                    return (
                      <tr key={contract.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {contract.contract_number || `#${contract.id?.slice(0, 8)}`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{contract.customer_name || 'N/A'}</p>
                            <p className="text-sm text-gray-500">{contract.customer_email || ''}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {contract.contract_type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(contract.status)}`}>
                            <SafeIcon icon={StatusIcon} className="w-3 h-3" />
                            {contract.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(contract.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex gap-2">
                            <button
                              onClick={() => window.open(`/contracts/${contract.id}`, '_blank')}
                              className="text-primary-600 hover:text-primary-700"
                              title="View"
                            >
                              <SafeIcon icon={FiEye} className="w-4 h-4" />
                            </button>
                            {['sent', 'viewed'].includes(contract.status) && (
                              <button
                                onClick={() => resendContract(contract.id)}
                                className="text-blue-600 hover:text-blue-700"
                                title="Resend"
                              >
                                <SafeIcon icon={FiMail} className="w-4 h-4" />
                              </button>
                            )}
                            {!['completed', 'voided'].includes(contract.status) && (
                              <button
                                onClick={() => voidContract(contract.id)}
                                className="text-red-600 hover:text-red-700"
                                title="Void"
                              >
                                <SafeIcon icon={FiX} className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractDashboard;
